import { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  selectTherapistProfile,
  selectClinicId,
  selectTherapistId,
} from "../../../store/slices/authSlice";
import {
  useGetTasksQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
  useGetRemindersQuery,
  useCreateReminderMutation,
  useUpdateReminderMutation,
  useDeleteReminderMutation,
  useGetCategoriesQuery,
} from "../../../store/services/tasksApi";
import { useGetClinicTherapistsQuery } from "../../../store/services/therapistApi";
import RichTextEditor from "../../../components/ui/RichTextEditor";
import ConfirmDialog from "../../../components/ui/ConfirmDialog";
import DatePicker from "../../../components/ui/DatePicker";
import TimePicker from "../../../components/ui/TimePicker";
import {
  ListChecks,
  Plus,
  Check,
  Clock,
  Hourglass,
  CircleDashed,
  X,
  Pencil,
  Trash,
  CalendarBlank,
  User,
  ClockCounterClockwise,
  Note,
  CaretDown,
  Tag,
  ChatCircleText,
} from "@phosphor-icons/react";
import { HelpButton } from "../../../components/shared";
import ContentLoader from "../../../components/shared/ContentLoader";
import LoaderLoop from "../../../components/shared/loader/LoaderLoop";
import ModalCloseButton from "../../../components/ui/ModalCloseButton";
import CustomClinikitActionButton from "../../../components/ui/CustomClinikitActionButton";
import "../TasksRemindersPage.css";

// Task due dates are built in local time (see buildTaskDueAt in firebase.js),
// so derive the form values from the local Date, not from the UTC ISO string.
const extractDateFromIso = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const extractTimeFromIso = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

// Status configuration - UI order (dropdowns, filters)
const TASK_STATUSES = [
  { value: "todo", label: "פתוח", icon: CircleDashed, color: "#4dd9b4" },
  { value: "in_progress", label: "בתהליך", icon: Hourglass, color: "#3b82f6" },
  { value: "waiting", label: "ממתין", icon: Clock, color: "#f59e0b" },
  { value: "done", label: "הסתיים", icon: Check, color: "#22c55e" },
];

// Sort order for "all" view display (בתהליך first, then ממתין, then פתוח, then הסתיים)
const STATUS_SORT_ORDER = {
  in_progress: 0,
  waiting: 1,
  todo: 2,
  done: 3,
};

// Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  return date.toLocaleDateString("he-IL", {
    day: "numeric",
    month: "short",
  });
};

// Format datetime for history
const formatDateTime = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("he-IL", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Check if due date is past
const isDueDatePast = (dateStr) => {
  if (!dateStr) return false;
  const dueDate = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return dueDate < today;
};

function AdminTasksPage() {
  const therapistProfile = useSelector(selectTherapistProfile);
  const clinicId = useSelector(selectClinicId);
  const therapistId = useSelector(selectTherapistId);

  // Main tabs: tasks or reminders
  const [activeMainTab, setActiveMainTab] = useState("tasks");
  // Status filter for tasks
  const [statusFilter, setStatusFilter] = useState("all");

  // Modal states
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editingReminder, setEditingReminder] = useState(null);
  const [historyData, setHistoryData] = useState(null);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    type: "danger",
    icon: "delete",
    title: "",
    message: "",
    onConfirm: () => {},
  });

  // Form states
  const [taskForm, setTaskForm] = useState({
    title: "",
    note: "",
    status: "todo",
    dueDate: "",
    dueTime: "",
    assignedTo: "",
    categoryId: "",
  });
  const [reminderForm, setReminderForm] = useState({
    title: "",
    content: "",
    categoryId: "",
  });

  // Queries - Clinic tasks/reminders
  const { data: clinicTasks = [], isLoading: isLoadingTasks } = useGetTasksQuery(
    { clinicId, scope: "clinic" },
    { skip: !clinicId }
  );

  const { data: clinicReminders = [], isLoading: isLoadingReminders } = useGetRemindersQuery(
    { clinicId, scope: "clinic" },
    { skip: !clinicId }
  );

  const { data: taskCategories = [] } = useGetCategoriesQuery(
    { clinicId, type: "task" },
    { skip: !clinicId }
  );

  const { data: reminderCategories = [] } = useGetCategoriesQuery(
    { clinicId, type: "reminder" },
    { skip: !clinicId }
  );

  const { data: therapists = [] } = useGetClinicTherapistsQuery(
    { clinicId },
    { skip: !clinicId }
  );

  // Mutations
  const [createTask, { isLoading: isCreatingTask }] = useCreateTaskMutation();
  const [updateTask, { isLoading: isUpdatingTask }] = useUpdateTaskMutation();
  const [deleteTask] = useDeleteTaskMutation();
  const [createReminder, { isLoading: isCreatingReminder }] = useCreateReminderMutation();
  const [updateReminder, { isLoading: isUpdatingReminder }] = useUpdateReminderMutation();
  const [deleteReminder] = useDeleteReminderMutation();

  // Sort tasks by status priority
  const sortTasksByStatus = (tasks) => {
    return [...tasks].sort((a, b) => {
      const orderA = STATUS_SORT_ORDER[a.status] ?? 99;
      const orderB = STATUS_SORT_ORDER[b.status] ?? 99;
      return orderA - orderB;
    });
  };

  // Filter and sort tasks by status
  const filteredTasks = useMemo(() => {
    let tasks = clinicTasks;
    if (statusFilter !== "all") {
      tasks = tasks.filter((task) => task.status === statusFilter);
    }
    return sortTasksByStatus(tasks);
  }, [clinicTasks, statusFilter]);

  // Count tasks by status
  const taskCounts = useMemo(() => {
    const counts = { all: clinicTasks.length };
    TASK_STATUSES.forEach((status) => {
      counts[status.value] = clinicTasks.filter((t) => t.status === status.value).length;
    });
    return counts;
  }, [clinicTasks]);

  // Handlers
  const handleOpenTaskModal = (task = null) => {
    if (task) {
      setEditingTask(task);
      setTaskForm({
        title: task.title || "",
        note: task.note || "",
        status: task.status || "todo",
        dueDate: extractDateFromIso(task.dueDate),
        dueTime: task.hasDueTime ? extractTimeFromIso(task.dueDate) : "",
        assignedTo: task.assignedTo || "",
        categoryId: task.categoryId || "",
      });
    } else {
      setEditingTask(null);
      setTaskForm({
        title: "",
        note: "",
        status: "todo",
        dueDate: "",
        dueTime: "",
        assignedTo: "",
        categoryId: "",
      });
    }
    setShowTaskModal(true);
  };

  const handleOpenReminderModal = (reminder = null) => {
    if (reminder) {
      setEditingReminder(reminder);
      setReminderForm({
        title: reminder.title || "",
        content: reminder.content || "",
        categoryId: reminder.categoryId || "",
      });
    } else {
      setEditingReminder(null);
      setReminderForm({
        title: "",
        content: "",
        categoryId: "",
      });
    }
    setShowReminderModal(true);
  };

  const handleCloseTaskModal = () => {
    setShowTaskModal(false);
    setEditingTask(null);
    setTaskForm({
      title: "",
      note: "",
      status: "todo",
      dueDate: "",
      dueTime: "",
      assignedTo: "",
      categoryId: "",
    });
  };

  const handleCloseReminderModal = () => {
    setShowReminderModal(false);
    setEditingReminder(null);
    setReminderForm({
      title: "",
      content: "",
      categoryId: "",
    });
  };

  const handleSaveTask = async () => {
    if (!taskForm.title.trim()) return;

    const taskData = {
      title: taskForm.title.trim(),
      note: taskForm.note.trim() || null,
      status: taskForm.status,
      dueDate: taskForm.dueDate || null,
      dueTime: taskForm.dueDate && taskForm.dueTime ? taskForm.dueTime : null,
      assignedTo: taskForm.assignedTo || null,
      categoryId: taskForm.categoryId || null,
      scope: "clinic",
    };

    try {
      if (editingTask) {
        await updateTask({
          clinicId,
          taskId: editingTask.id,
          taskData,
          editorId: therapistId,
          editorName: `${therapistProfile.firstName} ${therapistProfile.lastName}`,
        }).unwrap();
      } else {
        await createTask({
          clinicId,
          taskData,
          creatorId: therapistId,
          creatorName: `${therapistProfile.firstName} ${therapistProfile.lastName}`,
        }).unwrap();
      }
      handleCloseTaskModal();
    } catch (error) {
      console.error("[CK-4101] Error saving clinic task:", {
        errorCode: "CK-4101",
        errorMessage: error.message,
        clinicId,
        taskId: editingTask?.id,
        isEditing: !!editingTask,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleSaveReminder = async () => {
    if (!reminderForm.title.trim()) return;

    const reminderData = {
      title: reminderForm.title.trim(),
      content: reminderForm.content || "",
      categoryId: reminderForm.categoryId || null,
      scope: "clinic",
    };

    try {
      if (editingReminder) {
        await updateReminder({
          clinicId,
          reminderId: editingReminder.id,
          reminderData,
          editorId: therapistId,
          editorName: `${therapistProfile.firstName} ${therapistProfile.lastName}`,
        }).unwrap();
      } else {
        await createReminder({
          clinicId,
          reminderData,
          creatorId: therapistId,
          creatorName: `${therapistProfile.firstName} ${therapistProfile.lastName}`,
        }).unwrap();
      }
      handleCloseReminderModal();
    } catch (error) {
      console.error("[CK-4102] Error saving clinic reminder:", {
        errorCode: "CK-4102",
        errorMessage: error.message,
        clinicId,
        reminderId: editingReminder?.id,
        isEditing: !!editingReminder,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleDeleteTask = (task) => {
    setConfirmDialog({
      isOpen: true,
      type: "danger",
      icon: "delete",
      title: "מחיקת משימה",
      message: `האם אתה בטוח שברצונך למחוק את המשימה "${task.title}"?`,
      onConfirm: async () => {
        try {
          await deleteTask({ clinicId, taskId: task.id }).unwrap();
        } catch (error) {
          console.error("[CK-4103] Error deleting clinic task:", {
            errorCode: "CK-4103",
            errorMessage: error.message,
            clinicId,
            taskId: task.id,
            timestamp: new Date().toISOString(),
          });
        }
      },
    });
  };

  const handleDeleteReminder = (reminder) => {
    setConfirmDialog({
      isOpen: true,
      type: "danger",
      icon: "delete",
      title: "מחיקת תזכורת",
      message: `האם אתה בטוח שברצונך למחוק את התזכורת "${reminder.title}"?`,
      onConfirm: async () => {
        try {
          await deleteReminder({ clinicId, reminderId: reminder.id }).unwrap();
        } catch (error) {
          console.error("[CK-4104] Error deleting clinic reminder:", {
            errorCode: "CK-4104",
            errorMessage: error.message,
            clinicId,
            reminderId: reminder.id,
            timestamp: new Date().toISOString(),
          });
        }
      },
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
  };

  const handleQuickStatusChange = async (task, newStatus) => {
    try {
      await updateTask({
        clinicId,
        taskId: task.id,
        taskData: { ...task, status: newStatus },
        editorId: therapistId,
        editorName: `${therapistProfile.firstName} ${therapistProfile.lastName}`,
      }).unwrap();
    } catch (error) {
      console.error("[CK-4105] Error updating clinic task status:", {
        errorCode: "CK-4105",
        errorMessage: error.message,
        clinicId,
        taskId: task.id,
        newStatus,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleShowHistory = (item) => {
    setHistoryData(item);
    setShowHistoryModal(true);
  };

  const getTherapistName = (uid) => {
    const therapist = therapists.find((t) => t.uid === uid);
    return therapist ? `${therapist.firstName} ${therapist.lastName}` : "";
  };

  const getCategoryById = (categoryId, type) => {
    const categories = type === "task" ? taskCategories : reminderCategories;
    return categories.find((c) => c.id === categoryId);
  };

  return (
    <div className="tasks-reminders-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-title">
          <ListChecks size={28} weight="duotone" className="header-icon" />
          <h1>ניהול משימות ותזכורות</h1>
        </div>
        <HelpButton featureKey="admin_tasks" />
      </div>

      {/* Main Tabs */}
      <div className="main-tabs">
        <button
          className={`main-tab ${activeMainTab === "tasks" ? "active" : ""}`}
          onClick={() => setActiveMainTab("tasks")}
        >
          <ListChecks size={20} weight="regular" />
          <span>משימות</span>
        </button>
        <button
          className={`main-tab ${activeMainTab === "reminders" ? "active" : ""}`}
          onClick={() => setActiveMainTab("reminders")}
        >
          <Note size={20} weight="regular" />
          <span>תזכורות</span>
        </button>
      </div>

      {/* Content Area */}
      <div className="content-area">
        {/* Header with Add Button */}
        <div className="content-header">
          <div className="content-header-spacer" />
          <CustomClinikitActionButton
            icon={<Plus size={16} weight="bold" />}
            onClick={() =>
              activeMainTab === "tasks"
                ? handleOpenTaskModal()
                : handleOpenReminderModal()
            }
          >
            {activeMainTab === "tasks" ? "משימה חדשה" : "תזכורת חדשה"}
          </CustomClinikitActionButton>
        </div>

        {/* Tasks View */}
        {activeMainTab === "tasks" && (
          <div className="tasks-section">
            {/* Status Filter */}
            <div className="status-filter">
              <button
                className={`filter-btn ${statusFilter === "all" ? "active" : ""}`}
                onClick={() => setStatusFilter("all")}
              >
                הכל ({taskCounts.all})
              </button>
              {TASK_STATUSES.map((status) => (
                <button
                  key={status.value}
                  className={`filter-btn ${statusFilter === status.value ? "active" : ""}`}
                  onClick={() => setStatusFilter(status.value)}
                  style={{ "--filter-color": status.color }}
                >
                  <status.icon size={14} weight="bold" />
                  <span>{status.label}</span>
                  <span className="filter-count">({taskCounts[status.value]})</span>
                </button>
              ))}
            </div>

            {/* Tasks List */}
            {isLoadingTasks ? (
              <ContentLoader message="טוען משימות..." />
            ) : filteredTasks.length === 0 ? (
              <div className="empty-state">
                <ListChecks size={48} weight="duotone" />
                <p>אין משימות קליניקה {statusFilter !== "all" ? "בסטטוס זה" : ""}</p>
                <button className="add-btn-secondary" onClick={() => handleOpenTaskModal()}>
                  <Plus size={18} weight="bold" />
                  <span>הוסף משימה</span>
                </button>
              </div>
            ) : (
              <div className="tasks-list">
                {filteredTasks.map((task) => {
                  const statusConfig = TASK_STATUSES.find((s) => s.value === task.status);
                  const category = getCategoryById(task.categoryId, "task");
                  const isPastDue = isDueDatePast(task.dueDate) && task.status !== "done";

                  return (
                    <div
                      key={task.id}
                      className={`task-card ${task.status === "done" ? "completed" : ""}`}
                    >
                      {/* Status Indicator */}
                      <div
                        className="task-status-indicator"
                        style={{ backgroundColor: statusConfig?.color }}
                      />

                      {/* Task Content */}
                      <div className="task-content">
                        <div className="task-header">
                          <h3 className="task-title">{task.title}</h3>
                          {category && (
                            <span
                              className="task-category"
                              style={{ backgroundColor: category.color + "20", color: category.color }}
                            >
                              <Tag size={12} weight="bold" />
                              {category.name}
                            </span>
                          )}
                        </div>

                        {task.note && (
                          <p className="task-note">
                            <ChatCircleText size={14} weight="regular" />
                            {task.note}
                          </p>
                        )}

                        <div className="task-meta">
                          {task.dueDate && (
                            <span className={`task-due ${isPastDue ? "past-due" : ""}`}>
                              <CalendarBlank size={14} weight="regular" />
                              {formatDate(task.dueDate)}
                            </span>
                          )}
                          {task.assignedTo && (
                            <span className="task-assignee">
                              <User size={14} weight="regular" />
                              {getTherapistName(task.assignedTo)}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Quick Status Change */}
                      <div className="task-status-select">
                        <select
                          value={task.status}
                          onChange={(e) => handleQuickStatusChange(task, e.target.value)}
                        >
                          {TASK_STATUSES.map((status) => (
                            <option key={status.value} value={status.value}>
                              {status.label}
                            </option>
                          ))}
                        </select>
                        <CaretDown size={14} weight="bold" className="select-icon" />
                      </div>

                      {/* Actions */}
                      <div className="task-actions">
                        {task.editHistory?.length > 0 && (
                          <button
                            className="action-btn"
                            onClick={() => handleShowHistory(task)}
                            title="היסטוריית עריכה"
                          >
                            <ClockCounterClockwise size={18} weight="regular" />
                          </button>
                        )}
                        <button
                          className="action-btn"
                          onClick={() => handleOpenTaskModal(task)}
                          title="ערוך"
                        >
                          <Pencil size={18} weight="regular" />
                        </button>
                        <button
                          className="action-btn delete"
                          onClick={() => handleDeleteTask(task)}
                          title="מחק"
                        >
                          <Trash size={18} weight="regular" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Reminders View */}
        {activeMainTab === "reminders" && (
          <div className="reminders-section">
            {isLoadingReminders ? (
              <ContentLoader message="טוען תזכורות..." />
            ) : clinicReminders.length === 0 ? (
              <div className="empty-state">
                <Note size={48} weight="duotone" />
                <p>אין תזכורות קליניקה</p>
                <button className="add-btn-secondary" onClick={() => handleOpenReminderModal()}>
                  <Plus size={18} weight="bold" />
                  <span>הוסף תזכורת</span>
                </button>
              </div>
            ) : (
              <div className="reminders-grid">
                {clinicReminders.map((reminder) => {
                  const category = getCategoryById(reminder.categoryId, "reminder");

                  return (
                    <div key={reminder.id} className="reminder-card">
                      <div className="reminder-header">
                        <h3 className="reminder-title">{reminder.title}</h3>
                        {category && (
                          <span
                            className="reminder-category"
                            style={{ backgroundColor: category.color + "20", color: category.color }}
                          >
                            <Tag size={12} weight="bold" />
                            {category.name}
                          </span>
                        )}
                      </div>

                      {reminder.content && (
                        <div
                          className="reminder-content rich-text-display"
                          dangerouslySetInnerHTML={{ __html: reminder.content }}
                        />
                      )}

                      <div className="reminder-footer">
                        <span className="reminder-date">
                          {formatDate(reminder.createdAt)}
                        </span>
                        <div className="reminder-actions">
                          {reminder.editHistory?.length > 0 && (
                            <button
                              className="action-btn"
                              onClick={() => handleShowHistory(reminder)}
                              title="היסטוריית עריכה"
                            >
                              <ClockCounterClockwise size={16} weight="regular" />
                            </button>
                          )}
                          <button
                            className="action-btn"
                            onClick={() => handleOpenReminderModal(reminder)}
                            title="ערוך"
                          >
                            <Pencil size={16} weight="regular" />
                          </button>
                          <button
                            className="action-btn delete"
                            onClick={() => handleDeleteReminder(reminder)}
                            title="מחק"
                          >
                            <Trash size={16} weight="regular" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Task Modal */}
      {showTaskModal && (
        <div className="modal-overlay" onClick={handleCloseTaskModal}>
          <div className="modal task-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingTask ? "עריכת משימה" : "משימה חדשה"}</h3>
              <ModalCloseButton onClick={handleCloseTaskModal} />
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label className="required">כותרת</label>
                <input
                  type="text"
                  value={taskForm.title}
                  onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                  placeholder="הכנס כותרת למשימה"
                />
              </div>

              <div className="form-group">
                <label>הערה</label>
                <textarea
                  value={taskForm.note}
                  onChange={(e) => setTaskForm({ ...taskForm, note: e.target.value })}
                  placeholder="הוסף הערה (אופציונלי)"
                  rows={3}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>סטטוס</label>
                  <select
                    value={taskForm.status}
                    onChange={(e) => setTaskForm({ ...taskForm, status: e.target.value })}
                  >
                    {TASK_STATUSES.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>תאריך יעד</label>
                  <DatePicker
                    value={taskForm.dueDate}
                    onChange={(e) =>
                      setTaskForm({
                        ...taskForm,
                        dueDate: e.target.value,
                        dueTime: e.target.value ? taskForm.dueTime : "",
                      })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>שעת יעד</label>
                  <TimePicker
                    value={taskForm.dueTime}
                    disabled={!taskForm.dueDate}
                    onChange={(e) =>
                      setTaskForm({ ...taskForm, dueTime: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>שייך למטפל</label>
                  <select
                    value={taskForm.assignedTo}
                    onChange={(e) => setTaskForm({ ...taskForm, assignedTo: e.target.value })}
                  >
                    <option value="">ללא שיוך</option>
                    {therapists.filter((t) => t.isActive).map((therapist) => (
                      <option key={therapist.uid} value={therapist.uid}>
                        {therapist.firstName} {therapist.lastName}
                      </option>
                    ))}
                  </select>
                </div>

                {taskCategories.length > 0 && (
                  <div className="form-group">
                    <label>קטגוריה</label>
                    <select
                      value={taskForm.categoryId}
                      onChange={(e) => setTaskForm({ ...taskForm, categoryId: e.target.value })}
                    >
                      <option value="">ללא קטגוריה</option>
                      {taskCategories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseTaskModal}>
                ביטול
              </button>
              <button
                className="btn-primary"
                onClick={handleSaveTask}
                disabled={!taskForm.title.trim() || isCreatingTask || isUpdatingTask}
              >
                {(isCreatingTask || isUpdatingTask) && <LoaderLoop size="xs" />}
                <span>{editingTask ? "שמור שינויים" : "צור משימה"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reminder Modal */}
      {showReminderModal && (
        <div className="modal-overlay" onClick={handleCloseReminderModal}>
          <div className="modal reminder-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingReminder ? "עריכת תזכורת" : "תזכורת חדשה"}</h3>
              <ModalCloseButton onClick={handleCloseReminderModal} />
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label className="required">כותרת</label>
                <input
                  type="text"
                  value={reminderForm.title}
                  onChange={(e) => setReminderForm({ ...reminderForm, title: e.target.value })}
                  placeholder="הכנס כותרת לתזכורת"
                />
              </div>

              {reminderCategories.length > 0 && (
                <div className="form-group">
                  <label>קטגוריה</label>
                  <select
                    value={reminderForm.categoryId}
                    onChange={(e) => setReminderForm({ ...reminderForm, categoryId: e.target.value })}
                  >
                    <option value="">ללא קטגוריה</option>
                    {reminderCategories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>תוכן</label>
                <RichTextEditor
                  value={reminderForm.content}
                  onChange={(content) => setReminderForm({ ...reminderForm, content })}
                  placeholder="הכנס תוכן לתזכורת..."
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseReminderModal}>
                ביטול
              </button>
              <button
                className="btn-primary"
                onClick={handleSaveReminder}
                disabled={!reminderForm.title.trim() || isCreatingReminder || isUpdatingReminder}
              >
                {(isCreatingReminder || isUpdatingReminder) && <LoaderLoop size="xs" />}
                <span>{editingReminder ? "שמור שינויים" : "צור תזכורת"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistoryModal && historyData && (
        <div className="modal-overlay" onClick={() => setShowHistoryModal(false)}>
          <div className="modal history-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>היסטוריית עריכה</h3>
              <ModalCloseButton onClick={() => setShowHistoryModal(false)} />
            </div>

            <div className="modal-body">
              <h4 className="history-item-title">{historyData.title}</h4>
              
              {historyData.editHistory?.length > 0 ? (
                <div className="history-list">
                  {[...historyData.editHistory].reverse().map((entry, index) => (
                    <div key={index} className="history-entry">
                      <div className="history-entry-header">
                        <span className="history-editor">{entry.editorName}</span>
                        <span className="history-date">{formatDateTime(entry.editedAt)}</span>
                      </div>
                      <div className="history-changes">
                        {Object.entries(entry.changes.after).map(([key, value]) => {
                          const before = entry.changes.before[key];
                          if (before === value) return null;
                          
                          const fieldNames = {
                            title: "כותרת",
                            note: "הערה",
                            content: "תוכן",
                            status: "סטטוס",
                            assignedTo: "שיוך",
                            categoryId: "קטגוריה",
                            dueDate: "תאריך יעד",
                          };

                          return (
                            <div key={key} className="change-item">
                              <span className="change-field">{fieldNames[key] || key}:</span>
                              <span className="change-value">
                                {before || "(ריק)"} → {value || "(ריק)"}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-history">אין היסטוריית עריכה</p>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowHistoryModal(false)}>
                סגור
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={closeConfirmDialog}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        icon={confirmDialog.icon}
        confirmText="מחק"
      />
    </div>
  );
}

export default AdminTasksPage;
